import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { sessionStorageKeys } from 'constants/common'
import styles from './index.module.scss'
import _compact from 'lodash/compact'
import ListCardBadge from './ListCardBadge'
import _upperFirst from 'lodash/upperFirst'
import _isEmpty from 'lodash/isEmpty'
import useProviderListingDetails from 'hooks/provider/useProviderListingDetails-v3'
import useAppSelector from 'hooks/useAppSelector'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { getCombinedAgeRange } from 'app/ProviderDetails/utils'
import { ageGroupServed } from 'app/Bookings/utils'
import useThriveAuth from 'hooks/useThriveAuth'
import { formatProviderPronouns } from 'utils/formatProviderPronouns'
import {
  getParsedQuery,
  getPracticeAddressFeatures,
  getStringifiedQuery,
} from 'utils/common'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useState } from 'react'
import credentials from 'common/ProviderCredentials/credentials'
import { getHighlightingInfo } from 'components/app/ProviderSearch/utils'
import { InsuranceProviderContext } from 'context/common/InsuranceProvider'
import {
  renderInsuranceSection,
  renderSpecialtySection,
} from './renderHighlightedText'
import { TimeOfDay } from 'constants/common'
import BonusBadge from './BonusBadge'
import {
  getSearchMatchSlotLocation,
  PROVIDER_PROFILE_SLOT_LOCATION_QUERY,
} from 'app/ProviderSearch/constants'

type ListCardProviderSectionProps = {
  providerDetails: Provider
  practice: Practice
  renderCustomInsuranceSection?: () => JSX.Element
  matchedBonusTimeOfDay?: TimeOfDay[]
  slotLocation?: string
}

const ListCardProviderSection: React.FC<ListCardProviderSectionProps> = ({
  providerDetails,
  practice,
  renderCustomInsuranceSection,
  matchedBonusTimeOfDay,
  slotLocation,
}) => {
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const router = useRouter()

  const { query } = router
  const [isAvailabilityInPerson, setIsAvailabilityInPerson] = useState<boolean>(
    false
  )

  const clientAgeGroupFilters =
    globalGroupedFilters[ProviderFilterTypes.CLIENT_AGE_GROUP]

  const { insuranceProvider } = useContext(InsuranceProviderContext)

  // Get highlighting information from URL query parameters (for existing specialty highlighting)
  const { selectedSpecialtyIds } = getHighlightingInfo(router)

  // Get area of concern filters for specialty highlighting
  const areaOfConcernFilters =
    globalGroupedFilters[ProviderFilterTypes.AREA_OF_CONCERN] || []

  // Convert selected specialty IDs to names for highlighting
  const selectedSpecialtyNames = selectedSpecialtyIds
    .map((id) => areaOfConcernFilters.find((filter) => filter.id === id)?.value)
    .filter(Boolean)

  const {
    firstName,
    lastName,
    pronouns,
    profileImagePath,
    yearsOfExperience,
    title,
    clientAgeGroupIds,
    tagIds,
  } = providerDetails ?? {}
  const {
    availabilityTags,
    areaOfConcernValues,
    allAreasOfConcernValues,
    insuranceNamesValues,
  } = useProviderListingDetails(providerDetails, isSupportOrThirdPartyUser)
  const {
    city,
    state,
    streetAddress,
    aptOrSuite,
    zipcode,
  } = getPracticeAddressFeatures(practice)

  const providerCredentials = credentials[title] || {}
  const fullCredentials = providerCredentials?.definition
  const providerProfileLink = getProviderProfileRoute(providerDetails)
  const formattedQuery = getParsedQuery(router)
  const searchMatchSlotLocation = getSearchMatchSlotLocation(slotLocation)
  const queryObject = {
    ...(query?.insuranceId && { insuranceId: query.insuranceId }),
    ...(practice?.distanceInMiles && {
      distanceInMiles: practice?.distanceInMiles?.toFixed(2),
    }),
    ...(!_isEmpty(formattedQuery?.counselingType) && {
      counselingType: formattedQuery.counselingType,
    }),
    ...(!_isEmpty(formattedQuery?.availabilityType) && {
      availabilityType: formattedQuery.availabilityType[0],
    }),
    ...(!_isEmpty(formattedQuery?.areaOfConcern) && {
      areaOfConcern: formattedQuery.areaOfConcern,
    }),
    ...(!_isEmpty(formattedQuery?.timeOfDay) && {
      timeOfDay: formattedQuery.timeOfDay,
    }),
    ...(!_isEmpty(formattedQuery?.partnershipCode) && {
      partnershipCode: formattedQuery.partnershipCode,
    }),
    ...(searchMatchSlotLocation && {
      [PROVIDER_PROFILE_SLOT_LOCATION_QUERY]: searchMatchSlotLocation,
    }),
  }

  const serializedQueryObject = getStringifiedQuery(queryObject)

  const availabilityTypeFilters =
    globalGroupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]

  useEffect(() => {
    availabilityTypeFilters.map((avail) => {
      if (
        avail.id === queryObject?.availabilityType &&
        avail.value === 'In Person'
      ) {
        setIsAvailabilityInPerson(true)
      }
    })
  }, [queryObject])

  const ageGroups = clientAgeGroupFilters
    .filter((clientAgeGroupFilter) =>
      clientAgeGroupIds?.includes(clientAgeGroupFilter.id)
    )
    .map((clientAgeGroupFilter) => clientAgeGroupFilter.value)

  const communityExperience = globalGroupedFilters[ProviderFilterTypes.TAG]
  const communityTag = communityExperience
    ?.filter((tag) => tagIds?.includes(tag.id))
    .map((tag) => tag.value)
  const formattedPronouns = formatProviderPronouns(pronouns)

  // Store selected specialty IDs in session storage before navigation
  const handleProviderCardClick = () => {
    if (selectedSpecialtyIds.length > 0) {
      sessionStorage.setItem(
        sessionStorageKeys.SELECTED_SPECIALTY_IDS,
        JSON.stringify(selectedSpecialtyIds)
      )
    }
  }

  return (
    <div className={styles.ProviderCard}>
      <Link
        href={{
          pathname: providerProfileLink,
          query: serializedQueryObject,
        }}
        passHref
        legacyBehavior
      >
        <a
          href=" "
          className="text-decoration-none"
          onClick={handleProviderCardClick}
        >
          <div className={styles.ProviderCard_container}>
            <div className={styles.ProviderCard_container_header_providerImage}>
              <Image
                src={profileImagePath || DEFAULT_PROFILE_IMAGE_PATH}
                alt={`${firstName} ${lastName}'s profile image`}
                layout="fill"
                className="rounded-circle c-pointer"
                objectFit="cover"
                objectPosition="top"
                sizes="224px"
                quality={100}
              />
            </div>
            <div>
              <div className={styles.ProviderCard_container_header}>
                <div
                  className={styles.ProviderCard_container_header_providerInfo}
                >
                  <div
                    className={
                      styles.ProviderCard_container_header_providerInfo_container
                    }
                  >
                    <h3>
                      <span
                        className={
                          styles.ProviderCard_container_header_providerInfo_container_name
                        }
                      >
                        {`${firstName} ${lastName}`}{' '}
                      </span>
                      {/* Display pronouns with slash between sets (e.g. She/Her/They/Them) */}
                      {formattedPronouns && (
                        <span
                          className={
                            styles.ProviderCard_container_header_providerInfo_container_pronouns
                          }
                        >
                          {formattedPronouns}
                        </span>
                      )}
                    </h3>
                  </div>

                  <p className={styles.credentials}>
                    {fullCredentials && (
                      <span>
                        {`${fullCredentials} ${
                          title !== fullCredentials ? ` (${title})` : ''
                        } `}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className={styles.InfoContainer}>
                {renderCustomInsuranceSection
                  ? renderCustomInsuranceSection()
                  : insuranceNamesValues.length > 0 && (
                      <div className={styles.InfoContainer_insurance}>
                        <div className={styles.InfoContainer_insurance_icon}>
                          <img
                            src="/icons/shield-check.svg"
                            srcSet="/icons/shield-check1.webp"
                            alt="insurance"
                          />
                        </div>
                        <div className={styles.InfoContainer_insurance_list}>
                          {renderInsuranceSection(
                            insuranceProvider?.name,
                            providerDetails.insuranceNames,
                            insuranceNamesValues
                          )}
                        </div>
                      </div>
                    )}
                {allAreasOfConcernValues.length > 0 && (
                  <div className={styles.InfoContainer_speciality}>
                    <div className={styles.InfoContainer_speciality_icon}>
                      <img
                        src="/icons/armchair.svg"
                        srcSet="/icons/armchair.webp"
                        alt="speciality"
                      />
                    </div>
                    <div className={styles.InfoContainer_speciality_list}>
                      {renderSpecialtySection(
                        selectedSpecialtyNames,
                        allAreasOfConcernValues,
                        areaOfConcernValues,
                        providerDetails.areaOfConcernIds
                      )}
                    </div>
                  </div>
                )}
                {isSupportOrThirdPartyUser && (
                  <div className={styles.InfoContainer_availability}>
                    <div className={styles.InfoContainer_availability_icon}>
                      <img
                        src="/icons/map-pin.svg"
                        srcSet="/icons/map-pin.webp"
                        alt="address"
                      />
                    </div>

                    <div className={styles.InfoContainer_availability_list}>
                      <div>
                        {_compact([streetAddress, aptOrSuite]).join(', ')}
                      </div>
                      <div>
                        {city}, {state} {zipcode} |{' '}
                        {`${practice?.distanceInMiles?.toFixed(2)}mi`}
                      </div>
                    </div>
                  </div>
                )}
                {isAvailabilityInPerson && !isSupportOrThirdPartyUser && (
                  <div className={styles.InfoContainer_availability}>
                    <div className={styles.InfoContainer_availability_icon}>
                      <img
                        src="/icons/map-pin.svg"
                        srcSet="/icons/map-pin.webp"
                        alt="address"
                      />
                    </div>

                    <div className={styles.InfoContainer_availability_list}>
                      <div>
                        {city}, {state} |{' '}
                        {`${practice?.distanceInMiles?.toFixed(2)}mi`}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.BadgeContainer}>
                {!_isEmpty(availabilityTags) && (
                  <ListCardBadge>
                    {_upperFirst(availabilityTags.join(' & '))}
                  </ListCardBadge>
                )}

                {!!yearsOfExperience && (
                  <ListCardBadge>{`${yearsOfExperience} years in practice`}</ListCardBadge>
                )}

                <ListCardBadge>
                  {`Client ages: ${getCombinedAgeRange(
                    ageGroupServed(ageGroups)
                  )}`}
                </ListCardBadge>

                {!_isEmpty(communityTag) && (
                  <ListCardBadge>LGBTQIA+ experience</ListCardBadge>
                )}
              </div>

              {matchedBonusTimeOfDay && matchedBonusTimeOfDay.length > 0 && (
                <BonusBadge matchedTimeOfDay={matchedBonusTimeOfDay} />
              )}
            </div>
          </div>
        </a>
      </Link>
    </div>
  )
}

export default ListCardProviderSection
